import { Component, type ReactNode, type ErrorInfo } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

/** Detect chunk/module load failures caused by deploy cache invalidation */
function isChunkLoadError(error: Error): boolean {
  const msg = error.message?.toLowerCase() ?? ''
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('failed to load module script')
  )
}

const RELOAD_KEY = 'erp_chunk_reload'

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo)

    // Auto-reload once on chunk load errors (stale cache after deploy)
    if (isChunkLoadError(error)) {
      const lastReload = sessionStorage.getItem(RELOAD_KEY)
      const now = Date.now()
      // Only auto-reload if we haven't done it in the last 30 seconds (avoid loop)
      if (!lastReload || now - Number(lastReload) > 30_000) {
        sessionStorage.setItem(RELOAD_KEY, String(now))
        window.location.reload()
        return
      }
    }
  }

  render() {
    if (this.state.hasError) {
      const isChunk = this.state.error ? isChunkLoadError(this.state.error) : false

      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8">
          <div className="w-16 h-16 bg-danger-50 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-danger-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            {isChunk ? 'Aggiornamento disponibile' : 'Qualcosa è andato storto'}
          </h2>
          <p className="text-sm text-slate-500 mb-6 max-w-md">
            {isChunk
              ? 'È stata rilasciata una nuova versione. Ricarica la pagina per aggiornare.'
              : 'Si è verificato un errore imprevisto. Prova a ricaricare la pagina.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 px-4 py-2 bg-primary-900 text-white text-sm font-medium rounded-md hover:bg-primary-800 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Ricarica Pagina
          </button>
          {this.state.error && !isChunk && (
            <pre className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 max-w-lg overflow-x-auto text-left">
              {this.state.error.message}
            </pre>
          )}
        </div>
      )
    }

    return this.props.children
  }
}
